import gsap from 'gsap';
import { autobind } from 'core-decorators';

import config from '@resn/gozer-core/config';

import audioCore from '@resn/gozer-audio';
import AssetsManager from 'utils/loading/assets-manager';
import eventEmitter from '@resn/gozer-decorators/event-emitter-decorator';

const getAsset = (manifest, asset) => {
    if (typeof asset === 'string') {
        return AssetsManager.getAsset(manifest, asset);
    } else {
        return asset;
    }
};

@eventEmitter
class AudioManagerClass {
    constructor() {
        this.id = 0;
        this.gainNode = audioCore.context.createGain();
        this.gainNode.gain.value = 0;
        this.sounds = [];
        this.gainPool = [];
        this.panPool = [];
        this.currentAudio = 0;
        this.clonesPool = [];

        // number to limit the amount of sounds that update pan + volume on worldPosition per frame
        this.maxPositionalSoundsPerFrame = 1;

        if (config.withAudio) {
            this.gainNode.connect(audioCore.master);
        }
    }

    @autobind
    getGain() {
        for (let i = 0; i < this.gainPool.length; i++) {
            const gain = this.gainPool[i];
            if (!gain.inUse) {
                gain.inUse = true;
                return gain;
            }
        }
        const newGain = {
            object: audioCore.context.createGain(),
            inUse: true,
        };
        this.gainPool.push(newGain);

        return newGain;
    }

    // @autobind
    // stop(audio) {
    //     console.log('stop', audio)
    //     if (audio.currentTime !== 0) {
    //         audio.stop();
    //     }
    //     this.cleanupAfterAudio(audio);
    // }

    @autobind
    play(
        asset,
        {
            loop = false,
            volume = 1,
            fade = false,
            duration = 1,
            clone = false,
            emitter = null,
            playbackRate = null,
            manifest = 'core',
            onComplete = () => {},
        } = {},
    ) {
        let audio;
        this.id++;
        audio = getAsset(manifest, asset);

        if (clone) {
            const cloneAvailable = this.clonesPool.find((cloned) => {
                return cloned.asset === asset && cloned.audio.currentTime === 0;
            });

            if (cloneAvailable) {
                // console.log('clone available and ready for', asset);
                audio = cloneAvailable.audio;
            } else {
                // console.log('cloning', asset, this.clonesPool);
                audio = audio.clone();

                this.clonesPool.push({ id: Date.now(), asset, audio });
            }
        }

        audio.loop = loop;
        audio.volume = volume;
        audio.onEnded = this.onAudioComplete;
        audio.onComplete = onComplete;
        const success = audio.play(playbackRate);
        audio.obj = success.obj;

        if (success.success) {
            audio.id = this.id;
            audio.node.disconnect();
            if (emitter) {
                audio.emitter = emitter;
                audio.gain = this.getGain();
                audio.gainPower = 1;
                audio.node.connect(audio.gain.object);
                audio.gain.object.connect(this.gainNode);
                // }
                this.sounds.push(audio);
            } else {
                audio.node.connect(this.gainNode);
            }

            if (fade) {
                gsap.killTweensOf(audio);
                gsap.from(audio, {
                    duration,
                    volume: 0,
                });
            }
        }

        return audio;
    }

    @autobind
    onAudioComplete(audio) {
        if (audio.onComplete) {
            audio.onComplete();
        }
        this.cleanupAfterAudio(audio);
    }

    cleanupAfterAudio(audio) {
        if (audio.gain) {
            audio.gain.object.disconnect();
            audio.gain.inUse = false;
        }

        for (let i = 0; i < this.sounds.length; i++) {
            if (this.sounds[i].id == audio.id) {
                this.sounds.splice(i, 1);
            }
        }
        // console.log("this.sounds.length: ",len, '=>', this.sounds.length);
    }

    pause(asset, { fade = false, duration = 0.5, manifest = 'core' } = {}) {
        const audio = getAsset(manifest, asset);

        if (fade) {
            gsap.killTweensOf(audio);
            gsap.to(audio, {
                duration,
                volume: 0,
                onComplete: () => {
                    audio.stop();
                },
            });
        } else {
            audio.stop();
        }
    }

    setVolume(asset, { volume = 1, fade = true, duration = 0.5, manifest = 'core' }) {
        const audio = getAsset(manifest, asset);

        if (!audio) return;

        if (fade) {
            gsap.killTweensOf(audio);
            gsap.to(audio, {
                duration,
                volume,
            });
        } else {
            audio.volume = volume;
        }
    }

    fadeIn() {
        gsap.killTweensOf(this.gainNode.gain);
        gsap.to(this.gainNode.gain, { duration: 2, value: 1 });
        this.emit('fadeIn');
    }

    fadeOut() {
        gsap.killTweensOf(this.gainNode.gain);
        gsap.to(this.gainNode.gain, { duration: 0.75, value: 0 });
        this.emit('fadeOut');
    }
}

const AudioManager = new AudioManagerClass();

if (__DEV__) window.AudioManager = AudioManager;

export default AudioManager;
